import type { ReactNode } from "react"
import Badge from "@/components/Badge/Badge"
import { LOBBY_COPY } from "@/content/lobby"
import type { FamilyNumber } from "@/theme/colors"
import type { Participant } from "@/types"
import styles from "@/styles/components/SeatRing.module.css"

/*
 * The round table.
 *
 * Extracted from the lobby when the waiting screen needed the same object: a
 * guest who has taken a seat and the facilitator watching seats fill are looking
 * at the same room, and two layouts of it would read as two rooms.
 *
 * Seats sit on the circumference at even angles from the top, and the ring grows
 * with the roster rather than the seats crowding — a table that gets bigger as
 * people arrive is the metaphor, and it also keeps badges from overlapping at
 * eight or nine people.
 */

/* Big enough at zero seats that the centre — the status line and the share
 * link pill — clears the circumference: the ring narrows away from its
 * middle, so a box that only just fits the widest line still crosses the arc
 * above and below it. */
const BASE_SIZE = 324
const PER_SEAT = 40

/* Seats sit inside the dashed boundary, not on it — a badge centred on the
 * circumference itself reads as half-fallen-off. */
const SEAT_RADIUS = 38

/*
 * The first seat sits at the upper right, not straight up. Dead centre-top or
 * centre-bottom points a seat's badge (bottom seats) or its name/role line
 * (top seats) — whichever side of the badge hangs toward the middle — right at
 * the status line and the link pill, which is exactly where the ring is
 * narrowest at low seat counts. Every seat off that vertical axis instead.
 */
const START_ANGLE = -45

function seatPosition(index: number, total: number) {
  const angle = ((START_ANGLE + index * (360 / Math.max(total, 1))) * Math.PI) / 180
  return {
    left: `${(50 + SEAT_RADIUS * Math.cos(angle)).toFixed(2)}%`,
    top: `${(50 + SEAT_RADIUS * Math.sin(angle)).toFixed(2)}%`,
  }
}

interface SeatRingProps {
  participants: readonly Participant[]
  /** What sits inside the ring: the status, and whatever fills the table. */
  centre: ReactNode
  /** Marks this browser's own seat. */
  meId?: string | null
  /**
   * A dashed seat with nobody in it, at the end of the ring.
   *
   * Not "the roster is one short" — nothing in the model knows how many people
   * were invited. It says there is room for one more, which is the only true
   * thing an empty chair can mean here, and it is what keeps a lobby with three
   * seats from reading as a table that is already full.
   */
  showEmptySeat?: boolean
}

export default function SeatRing({
  participants,
  centre,
  meId,
  showEmptySeat = false,
}: SeatRingProps) {
  const seated = participants.length
  // The empty chair takes a place on the circumference like any other, or it
  // would be drawn on top of a real seat.
  const places = seated + (showEmptySeat ? 1 : 0)
  const size = BASE_SIZE + places * PER_SEAT

  /*
   * The height is derived from the width rather than set alongside it: the
   * stylesheet caps the width at `min(100%, 660px)`, and a fixed height would
   * survive that cap — leaving a tall ellipse on a narrow screen, or past nine
   * seats, instead of a table.
   */
  return (
    <section className={styles.wrap} style={{ width: size, aspectRatio: 1 }}>
      <div className={styles.ring} aria-hidden="true" />
      <div className={styles.middle}>{centre}</div>

      <ul className={styles.seats}>
        {participants.map((participant, index) => (
          <li
            key={participant.id}
            className={[
              styles.seat,
              participant.ready ? styles.seatReady : "",
              participant.id === meId ? styles.seatMe : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={seatPosition(index, places)}
          >
            <Badge
              name={participant.name}
              size={52}
              variant="person"
              family={((index % 7) + 1) as FamilyNumber}
            />
            <span className={styles.name}>{participant.name}</span>
            {participant.jobRole ? (
              <span className={styles.role}>{participant.jobRole}</span>
            ) : null}
          </li>
        ))}

        {showEmptySeat ? (
          <li className={styles.seat} style={seatPosition(seated, places)}>
            <span className={styles.vacant} aria-hidden="true" />
            <span className={styles.vacantName}>{LOBBY_COPY.emptySeat}</span>
          </li>
        ) : null}
      </ul>

      {seated === 0 ? <p className={styles.empty}>{LOBBY_COPY.emptySeats}</p> : null}
    </section>
  )
}
